import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as loginApi, type LoginParams } from '@/api/auth'
import { isTokenExpired } from '@/utils/auth'

const TOKEN_KEY = 'fire-iot-token'
const USER_ID_KEY = 'fire-iot-user-id'
const USERNAME_KEY = 'fire-iot-username'
const TENANT_ID_KEY = 'fire-iot-tenant-id'
const TENANT_NAME_KEY = 'fire-iot-tenant-name'
const ROLES_KEY = 'fire-iot-roles'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
    const userId = ref<number | null>(loadNumber(USER_ID_KEY))
    const username = ref<string | null>(localStorage.getItem(USERNAME_KEY))
    const tenantId = ref<number | null>(loadNumber(TENANT_ID_KEY))
    const tenantName = ref<string | null>(localStorage.getItem(TENANT_NAME_KEY))
    const roles = ref<string[]>(loadRoles())

    const isLoggedIn = computed(() => !!token.value)

    /**
     * 校验当前 token 是否有效，已过期则自动退出。
     */
    function validateSession(): boolean {
      if (!token.value) return false
      if (isTokenExpired(token.value)) {
        logout()
        return false
      }
      return true
    }

    function loadNumber(key: string): number | null {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const n = Number(raw)
      return Number.isNaN(n) ? null : n
    }

    function loadRoles(): string[] {
      try {
        const raw = localStorage.getItem(ROLES_KEY)
        return raw ? JSON.parse(raw) : []
      } catch {
        return []
      }
    }

    function hasRole(role: string): boolean {
      const candidates = new Set<string>()
      candidates.add(role)
      if (role.startsWith('ROLE_')) {
        candidates.add(role.slice(5).toLowerCase())
      } else {
        candidates.add(`ROLE_${role.toUpperCase()}`)
      }
      return roles.value.some((r) => candidates.has(r))
    }

    function hasAnyRole(requiredRoles: string[]): boolean {
      if (!requiredRoles || requiredRoles.length === 0) return true
      return requiredRoles.some((role) => hasRole(role))
    }

    function setAuth(data: {
      token: string
      userId?: number | null
      username?: string | null
      tenantId?: number | null
      tenantName?: string | null
      roles?: string[] | null
    }) {
      token.value = data.token
      userId.value = data.userId ?? null
      username.value = data.username ?? null
      tenantId.value = data.tenantId ?? null
      tenantName.value = data.tenantName ?? null
      roles.value = data.roles || []
      persistAuth()
    }

    function persistAuth() {
      if (token.value) {
        localStorage.setItem(TOKEN_KEY, token.value)
      } else {
        localStorage.removeItem(TOKEN_KEY)
      }
      if (userId.value != null) {
        localStorage.setItem(USER_ID_KEY, String(userId.value))
      } else {
        localStorage.removeItem(USER_ID_KEY)
      }
      if (username.value != null) {
        localStorage.setItem(USERNAME_KEY, username.value)
      } else {
        localStorage.removeItem(USERNAME_KEY)
      }
      if (tenantId.value != null) {
        localStorage.setItem(TENANT_ID_KEY, String(tenantId.value))
      } else {
        localStorage.removeItem(TENANT_ID_KEY)
      }
      if (tenantName.value != null) {
        localStorage.setItem(TENANT_NAME_KEY, tenantName.value)
      } else {
        localStorage.removeItem(TENANT_NAME_KEY)
      }
      localStorage.setItem(ROLES_KEY, JSON.stringify(roles.value))
    }

    function setToken(value: string) {
      token.value = value
      localStorage.setItem(TOKEN_KEY, value)
    }

    async function login(params: LoginParams) {
      const data = await loginApi(params)
      setAuth(data)
      return data
    }

    async function logout() {
      token.value = null
      userId.value = null
      username.value = null
      tenantId.value = null
      tenantName.value = null
      roles.value = []
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_ID_KEY)
      localStorage.removeItem(USERNAME_KEY)
      localStorage.removeItem(TENANT_ID_KEY)
      localStorage.removeItem(TENANT_NAME_KEY)
      localStorage.removeItem(ROLES_KEY)
    }

    return {
      token,
      userId,
      username,
      tenantId,
      tenantName,
      roles,
      isLoggedIn,
      hasRole,
      hasAnyRole,
      setToken,
      setAuth,
      login,
      logout,
      validateSession
    }
  }
)
